import { motion } from "framer-motion";
import { Page, PageHero, Eyebrow } from "@/components/PageShell";
import { COMPANY, IMAGES, TIMELINE } from "@/data/site";

export default function About() {
  return (
    <Page testid="about-page">
      <PageHero
        eyebrow="Company & Mirabaud Alliance"
        title="A Geneva house, built on two centuries of trust."
        subtitle="Lac Léman operates as the dedicated marketing branch of the Mirabaud Group — one of Switzerland's oldest private banks — serving its investment projects exclusively."
        image={IMAGES.genevaLake}
        testid="about-hero"
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-5 gap-16" data-testid="about-story">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-3"
        >
          <Eyebrow>Who We Are</Eyebrow>
          <h2 className="mt-5 font-serif-display text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-[#F8F9FA] leading-snug">
            The quiet engine behind Mirabaud's investment campaigns.
          </h2>
          <div className="mt-7 space-y-5 text-base leading-relaxed text-[#A0AAB2]">
            <p>
              Established in {COMPANY.operatingSince}, Lac Léman was created with a single,
              focused mandate: to give every Mirabaud investment project a marketing
              operation worthy of the Group's name. From our offices at Quai de l'Île 13
              in Geneva, we plan and execute capital-raising campaigns, investor
              positioning programmes, and strategic communications for the Group's
              projects worldwide.
            </p>
            <p>
              We are not a generalist agency. Every engagement we undertake serves a
              Mirabaud investment — whether a hydroelectric fund in the Andes, a
              sustainable forestry placement in the Amazon basin, solar infrastructure
              across East Africa, or a green bond financing alpine real estate here in
              Switzerland.
            </p>
            <p>
              Our work carries the discretion, precision, and finish that institutional
              investors expect from a Swiss private banking house — because that is
              exactly what stands behind us.
            </p>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="lg:col-span-2"
        >
          <div className="border border-[rgba(197,160,89,0.25)] bg-[#162248] p-8 lg:p-10" data-testid="about-facts-card">
            <Eyebrow>At a Glance</Eyebrow>
            <dl className="mt-8 space-y-7">
              {[
                ["Founded", COMPANY.operatingSince],
                ["Parent Group", `Mirabaud Group — Geneva, est. ${COMPANY.parentSince}`],
                ["Mandate", "Marketing for Mirabaud investment projects"],
                ["Headquarters", COMPANY.address],
                ["Footprint", "Latin America · Africa · Switzerland"],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#6C7A89]">
                    {k}
                  </dt>
                  <dd className="mt-1.5 text-base text-[#F8F9FA]">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </motion.div>
      </section>

      <section className="bg-[#101B3B] border-y border-[rgba(197,160,89,0.2)]" data-testid="about-alliance">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Eyebrow>The Mirabaud Alliance</Eyebrow>
            <h2 className="mt-5 font-serif-display text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-[#F8F9FA] leading-snug">
              1819 meets 2014.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#A0AAB2]">
              The Mirabaud Group has managed private wealth from Geneva since 1819.
              Lac Léman extends that legacy into the marketing realm — operating as the
              Group's in-house campaign capability so that every fund, bond, and
              placement speaks with one coherent, unmistakably Swiss voice.
            </p>
            <p className="mt-5 text-base leading-relaxed text-[#A0AAB2]">
              This alliance gives our work a rare advantage: direct access to the
              investment teams, complete alignment with the Group's compliance and brand
              standards, and the credibility of a name investors have trusted for over
              two hundred years.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative"
          >
            <div className="absolute -bottom-5 -right-5 w-full h-full border border-[rgba(197,160,89,0.3)]" />
            <img
              src={IMAGES.investmentBuilding}
              alt="Geneva financial architecture"
              className="relative w-full h-[420px] object-cover"
            />
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="mirabaud-timeline">
        <Eyebrow>Milestones</Eyebrow>
        <h2 className="mt-5 font-serif-display text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-[#F8F9FA]">
          From 1819 to today
        </h2>
        <div className="mt-14 relative">
          <div className="absolute left-[7px] top-1 bottom-1 w-px bg-[rgba(197,160,89,0.25)]" />
          <div className="space-y-12">
            {TIMELINE.map((t, i) => (
              <motion.div
                key={t.year}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative pl-12"
                data-testid={`timeline-item-${t.year}`}
              >
                <span className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 border-[#C5A059] bg-[#0B132B]" />
                <span className="font-mono-label text-sm tracking-[0.15em] text-[#E6C687]">{t.year}</span>
                <h3 className="mt-1.5 font-serif-display text-xl text-[#F8F9FA]">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#A0AAB2] max-w-2xl">{t.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </Page>
  );
}
